import { leaseStrings as s } from '@/features/lease/strings'
import type { DsTone } from '@/ds/tone'
import type { Lease, RentFrequency } from '@/types/models'
import { appLocale } from '@/i18n/locale.store'

export type LeaseFilter = 'all' | 'active' | 'endingSoon' | 'paymentPending'
export const LEASE_FILTERS: LeaseFilter[] = ['all', 'active', 'endingSoon', 'paymentPending']

const DAY = 86_400_000

/** Outstanding rent so far (mobile: `stat.expected − stat.collected`). */
export const leaseDue = (l: Lease) => Math.max((l.stat?.expected ?? 0) - (l.stat?.collected ?? 0), 0)

// Money a tenant recorded offline that the landlord has not accepted (`stat.pendingConfirmation`, the rents'
// `heldAmount`). The backend no longer counts it in `stat.collected` (LP-671), so `leaseDue` includes it.
export const awaitingConfirmation = (l: Lease) => Math.max(l.stat?.pendingConfirmation ?? 0, 0)

/** What is still owed beyond a claim awaiting the landlord: a held month reads "To confirm", not "Due" (LP-698). */
export const leaseOwed = (l: Lease) => Math.max(leaseDue(l) - awaitingConfirmation(l), 0)

export const tenantName = (l: Lease) => (l.tenant ? `${l.tenant.firstName} ${l.tenant.lastName}`.trim() : '')

export const isHistorical = (l: Lease) => l.status === 'expired' || l.status === 'canceled'

/** Whole days from today's midnight to the end date's midnight. */
export function daysLeft(endDate: string, now = new Date()): number {
  const end = new Date(endDate)
  const a = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const b = new Date(end.getFullYear(), end.getMonth(), end.getDate()).getTime()
  return Math.round((b - a) / DAY)
}

/**
 * "Expired" / "Ends today" / "N days left" (< 45) / "N months left": the one remaining-term label every
 * surface uses (Leases list, lease detail, tenant dashboard). Counted from the later of today and the
 * lease start, in whole 30-day months rounded down, so a lease that has not started shows its own term
 * and never more months than it has (LP-646, LP-700, LP-744).
 */
export function remainingLabel(endDate: string, now = new Date(), startDate?: string): string {
  const d = daysLeft(endDate, now)
  if (d < 0) return s.status.expired
  if (d === 0) return s.endsToday
  const left = startDate && daysLeft(startDate, now) > 0 ? daysLeft(endDate, new Date(startDate)) : d
  if (left < 45) return s.daysLeft(left)
  return s.monthsLeft(Math.floor(left / 30))
}

/** Term progress 0–1 between start and end. */
export function termProgress(startDate: string, endDate: string, now = new Date()): number {
  const a = Date.parse(startDate)
  const b = Date.parse(endDate)
  if (!(b > a)) return 1
  return Math.max(0, Math.min(1, (now.getTime() - a) / (b - a)))
}

/** Bar tone: brand, warning inside 30 days, danger once expired. */
export function termTone(endDate: string, now = new Date()): DsTone {
  const d = daysLeft(endDate, now)
  if (d < 0) return 'danger'
  if (d <= 30) return 'warning'
  return 'brand'
}

export function statusBadge(l: Lease): { label: string; tone: DsTone } {
  switch (l.status) {
    case 'payment-pending':
      return { label: s.paymentPendingBadge, tone: 'warning' }
    case 'canceled':
    case 'cancellation-requested':
      return { label: s.status[l.status], tone: 'danger' }
    case 'expired':
      return { label: s.status.expired, tone: 'neutral' }
    default:
      return { label: s.status.active, tone: 'success' }
  }
}

/** Months a lease of `duration` periods covers (weekly/daily rounded up), as the backend's curve does. */
export function durationMonths(duration: number, frequency: RentFrequency): number {
  switch (frequency) {
    case 'annually':
      return duration * 12
    case 'weekly':
      return Math.ceil((duration * 7) / 30)
    case 'daily':
      return Math.ceil(duration / 30)
    default:
      return duration
  }
}

/** `months` after `d`, clamped to the last day of the target month (31 Jan + 1 → 28 Feb), as the backend's utils.AddMonths. */
function addMonthsClamped(d: Date, months: number): Date {
  const target = new Date(d.getFullYear(), d.getMonth() + months, 1, d.getHours(), d.getMinutes(), d.getSeconds(), d.getMilliseconds())
  const last = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate()
  target.setDate(Math.min(d.getDate(), last))
  return target
}

/** End date = start + duration periods − 1 day (local); months and years clamp to the month end like the backend (LP-643). */
export function leaseEndDate(start: Date, duration: number, frequency: RentFrequency): Date {
  let d = new Date(start)
  switch (frequency) {
    case 'daily':
      d.setDate(d.getDate() + duration)
      break
    case 'weekly':
      d.setDate(d.getDate() + duration * 7)
      break
    case 'annually':
      d = addMonthsClamped(d, duration * 12)
      break
    default:
      d = addMonthsClamped(d, duration)
  }
  d.setDate(d.getDate() - 1)
  return d
}

const PERIOD_DAYS: Record<RentFrequency, number> = { daily: 1, weekly: 7, monthly: 30.4375, annually: 365.25 }

/** The whole number of periods (≥ 1) whose lease end lands nearest `end`: the inverse of leaseEndDate. */
export function durationBetween(start: Date, end: Date, frequency: RentFrequency): number {
  const days = (end.getTime() - start.getTime()) / 86_400_000 + 1
  const guess = Math.max(1, Math.round(days / PERIOD_DAYS[frequency]))
  let best = guess
  let bestDiff = Infinity
  for (let n = Math.max(1, guess - 2); n <= guess + 2; n++) {
    const diff = Math.abs(leaseEndDate(start, n, frequency).getTime() - end.getTime())
    if (diff < bestDiff) {
      best = n
      bestDiff = diff
    }
  }
  return best
}

/** The mobile duration presets: 11 months, 1 / 2 / 3 years (each re-derives the frequency). */
export const DURATION_PRESETS: { label: string; duration: number; frequency: RentFrequency }[] = [
  { label: '11 Month', duration: 11, frequency: 'monthly' },
  { label: '1 Year', duration: 12, frequency: 'monthly' },
  { label: '2 Year', duration: 24, frequency: 'monthly' },
  { label: '3 Year', duration: 36, frequency: 'monthly' },
]

const ym = new Intl.DateTimeFormat(appLocale(), { month: 'short', year: 'numeric' })
export const formatMonthYear = (d: Date | string) => ym.format(typeof d === 'string' ? new Date(d) : d)
const dmy = new Intl.DateTimeFormat(appLocale(), { day: '2-digit', month: 'short', year: 'numeric' })
export const formatDate = (d: Date | string) => dmy.format(typeof d === 'string' ? new Date(d) : d)

/** The ISO start the backend expects (`yyyy-MM-ddTHH:mm:ssZ` of local midnight). */
export const toStartIso = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString().replace(/\.\d{3}Z$/, 'Z')

/** The day a lease ended: an expired lease's scheduled end, a released lease's release day, or null for a lease released before that day was recorded (LP-648). */
export function endedDate(lease: Pick<Lease, 'status' | 'endDate' | 'canceledDate'>): string | null {
  if (lease.status === 'canceled') return lease.canceledDate ?? null
  return lease.endDate
}
